import { GOAL, clampX, createCircuit, startCircuit, pauseCircuit, stepCircuit } from "./sky-circuit-model.mjs";
import { BUILD_STEPS, assemblyState, silhouette, interval as region } from "./world-assembly.mjs";

/** Same generated assets in both blueprint and play states. No game engine dependency. */
export function initSkyCircuit(root, geometryChanged = () => {}) {
  const q = (selector) => root.querySelector(selector);
  const canvas = q("[data-world-canvas]");
  const ctx = canvas.getContext("2d");
  const model = createCircuit();
  const removers = [];
  const keys = new Set();
  const start = q("[data-world-start]");
  const overlay = q("[data-world-overlay]");
  const pauseButton = q("[data-world-pause]");
  const moves = Array.from(root.querySelectorAll("[data-world-move]"));
  let width = 1000, height = 600, progress = 0;
  let sprites = null, enabled = false, inView = false, disposed = false, loading = false;
  let frame = 0, last = 0, held = 0, lastState = "", lastEnergy = -1;
  const on = (element, name, callback, options) => {
    element.addEventListener(name, callback, options);
    removers.push(() => element.removeEventListener(name, callback, options));
  };

  // The atlas generator did not obey equal cells: these isolated regions avoid neighbours.
  const atlasRegions = { robot: [250, 25, 370, 490], energy: [1020, 70, 330, 410], ground: [0, 665, 980, 240], portal: [980, 520, 480, 480] };
  const extract = (image, [x, y, w, h]) => {
    const scratch = document.createElement("canvas");
    scratch.width = w; scratch.height = h;
    const c = scratch.getContext("2d", { willReadFrequently: true });
    c.drawImage(image, x, y, w, h, 0, 0, w, h);
    const pixels = c.getImageData(0, 0, w, h).data;
    let left = w, top = h, right = 0, bottom = 0;
    for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) {
      if (pixels[(yy * w + xx) * 4 + 3] > 180) {
        left = Math.min(left, xx); right = Math.max(right, xx);
        top = Math.min(top, yy); bottom = Math.max(bottom, yy);
      }
    }
    left = Math.max(0, left - 6); top = Math.max(0, top - 6);
    const sw = Math.min(w - left, right - left + 13), sh = Math.min(h - top, bottom - top + 13);
    const art = document.createElement("canvas"); art.width = sw; art.height = sh;
    art.getContext("2d").drawImage(scratch, left, top, sw, sh, 0, 0, sw, sh);
    // Derive the wireframe silhouette from this actual sprite, not unrelated mock art.
    const edge = document.createElement("canvas");
    edge.width = Math.min(512, sw); edge.height = Math.round(sh * edge.width / sw);
    const e = edge.getContext("2d", { willReadFrequently: true });
    e.drawImage(art, 0, 0, edge.width, edge.height);
    const source = e.getImageData(0, 0, edge.width, edge.height).data;
    return { art, ratio: sw / sh, contour: silhouette(source, edge.width, edge.height) };
  };
  const boxes = () => {
    const floor = height * 0.72;
    const robotH = Math.max(45, Math.min(120, height * 0.25, width * 0.17));
    const portalH = Math.max(76, Math.min(180, height * 0.4, width * 0.28));
    const crystalH = Math.max(25, Math.min(52, width * 0.045));
    const robotW = robotH * (sprites?.robot.ratio || 0.7);
    const portalW = portalH * (sprites?.portal.ratio || 0.9);
    return {
      ground: { x: width * 0.02, y: floor - 20, w: width * 0.96, h: Math.min(height * 0.18, width * 0.19) },
      robot: { x: model.player * width - robotW / 2, y: floor - robotH, w: robotW, h: robotH },
      portal: { x: width * 0.88 - portalW / 2, y: floor - portalH + 2, w: portalW, h: portalH },
      energy: { x: width * 0.52 - crystalH * 0.25, y: height * 0.59 - crystalH / 2, w: crystalH * (sprites?.energy.ratio || 0.5), h: crystalH },
    };
  };
  const contourFor = (name, box) => (sprites?.[name].contour || [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }]).map(point => ({ x: box.x + point.x * box.w, y: box.y + point.y * box.h }));
  const drawTrace = (points, amount) => {
    const lengths = points.map((a, i) => { const b = points[(i + 1) % points.length]; return Math.hypot(b.x - a.x, b.y - a.y); });
    let remaining = lengths.reduce((a, b) => a + b, 0) * amount;
    ctx.beginPath(); ctx.moveTo(points[0].x, points[0].y);
    for (let i = 0; i < points.length && remaining > 0; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      const t = lengths[i] ? Math.min(1, remaining / lengths[i]) : 1;
      ctx.lineTo(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
      remaining -= lengths[i];
    }
    ctx.stroke();
  };
  const drawSprite = (name, box, fill, wire = 0, trace = 1, flip = false) => {
    if (!sprites || trace <= 0) return;
    const sprite = sprites[name];
    ctx.save();
    ctx.translate(box.x + (flip ? box.w : 0), box.y);
    if (flip) ctx.scale(-1, 1);
    if (fill > 0) {
      ctx.save(); ctx.beginPath(); ctx.rect(-2, box.h * (1 - fill) - 2, box.w + 4, box.h * fill + 4); ctx.clip();
      ctx.drawImage(sprite.art, 0, 0, box.w, box.h); ctx.restore();
    }
    if (wire > 0) {
      ctx.globalAlpha = wire;
      ctx.strokeStyle = "#087bec"; ctx.lineWidth = width < 600 ? 1.35 : 2; ctx.lineJoin = "round";
      drawTrace(sprite.contour.map(point => ({ x: point.x * box.w, y: point.y * box.h })), trace);
      // Structural edges follow construction, rather than an image simply fading in.
      if (trace > 0.75) {
        ctx.strokeStyle = "#0b74e56b"; ctx.lineWidth = 1;
        const detail = region(trace, 0.75, 1);
        ctx.globalAlpha *= detail;
        ctx.beginPath();
        if (name === "ground") {
          ctx.moveTo(0, box.h * 0.24); ctx.lineTo(box.w, box.h * 0.24);
          for (let n = 0; n < 6; n++) {
            ctx.moveTo(n / 6 * box.w, box.h * 0.24);
            ctx.lineTo((n + 0.5) / 6 * box.w, box.h * 0.8);
            ctx.lineTo((n + 1) / 6 * box.w, box.h * 0.24);
          }
        } else if (name === "robot") {
          ctx.roundRect(box.w * 0.42, box.h * 0.21, box.w * 0.53, box.h * 0.28, box.w * 0.1);
          ctx.moveTo(box.w * 0.6, box.h * 0.32); ctx.lineTo(box.w * 0.6, box.h * 0.42);
          ctx.moveTo(box.w * 0.8, box.h * 0.32); ctx.lineTo(box.w * 0.8, box.h * 0.42);
        } else if (name === "energy") {
          ctx.moveTo(box.w * 0.5, 0); ctx.lineTo(box.w * 0.5, box.h);
          ctx.moveTo(0, box.h * 0.5); ctx.lineTo(box.w * 0.5, box.h * 0.3); ctx.lineTo(box.w, box.h * 0.5); ctx.lineTo(box.w * 0.5, box.h * 0.7); ctx.closePath();
        } else {
          ctx.moveTo(box.w * 0.23, box.h * 0.9); ctx.lineTo(box.w * 0.23, box.h * 0.35);
          ctx.bezierCurveTo(box.w * 0.23, box.h * 0.03, box.w * 0.8, box.h * 0.03, box.w * 0.8, box.h * 0.35);
          ctx.lineTo(box.w * 0.8, box.h * 0.9);
        }
        ctx.stroke();
      }
    }
    ctx.restore();
  };
  const draw = () => {
    if (!ctx || disposed) return;
    ctx.clearRect(0, 0, width, height);
    if (!sprites) return;
    const b = boxes();
    const construction = assemblyState(progress, height);
    const play = construction.stage === "play";
    const names = ["ground", "robot", "energy", "portal"];
    if (!play) {
      const blueprint = region(progress, 0.10, 0.19) * (1 - region(progress, 0.89, 0.98));
      if (blueprint > 0) {
        ctx.save(); ctx.globalAlpha = blueprint * 0.22; ctx.strokeStyle = "#0b74e5"; ctx.lineWidth = 1;
        const step = Math.max(28, width / 24);
        for (let x = 0; x < width; x += step) { ctx.beginPath(); ctx.moveTo(x, height * 0.12); ctx.lineTo(x, height * 0.95); ctx.stroke(); }
        for (let y = -(construction.camera % step); y < height; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke(); }
        ctx.restore();
      }
    }
    ctx.save();
    ctx.translate(0, construction.travel - construction.camera);
    names.forEach(name => {
      const step = BUILD_STEPS.find(step => step.key === name);
      const trace = play ? 1 : region(progress, step.traceStart, step.traceEnd);
      const fill = play ? 1 : construction.fill;
      const wire = play ? 0 : 1 - region(fill, 0.65, 1);
      if (name === "energy") {
        const drops = !play || model.state === "ready" ? [0.36, 0.52, 0.68].map(x => ({ x, y: 0.59 })) : model.drops;
        drops.forEach(drop => drawSprite(name, { ...b.energy, x: drop.x * width - b.energy.w / 2, y: drop.y * height - b.energy.h / 2 }, fill, wire, trace));
      } else {
        if (name === "portal" && play && model.energy < GOAL) ctx.globalAlpha = 0.55;
        drawSprite(name, b[name], fill, wire, trace, name === "robot" && model.facing < 0);
        ctx.globalAlpha = 1;
      }
    });
    for (const burst of model.bursts) {
      ctx.save(); ctx.globalAlpha = burst.life / 0.45; ctx.strokeStyle = "#0786e9"; ctx.lineWidth = 2;
      const r = (1 - burst.life / 0.45) * 35 + 5;
      ctx.beginPath(); ctx.arc(burst.x * width, burst.y * height, r, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
    }
    ctx.restore();
  };
  const sync = () => {
    const state = model.state;
    if (lastEnergy !== model.energy) {
      q("[data-world-score]").textContent = `${model.energy} / ${GOAL}`;
      q("[data-world-objective]").textContent = model.energy >= GOAL ? "Portal unlocked →" : "Collect the energy";
      if (model.energy >= GOAL) q("[data-world-announcement]").textContent = "Six cells collected. Move right to the portal.";
      lastEnergy = model.energy;
    }
    overlay.hidden = !enabled || state === "running";
    if (state === lastState) return;
    lastState = state; root.dataset.gameState = state;
    canvas.tabIndex = state === "running" ? 0 : -1;
    pauseButton.disabled = state === "ready" || state === "won";
    pauseButton.textContent = state === "paused" ? "Resume" : "Pause";
    moves.forEach(button => { button.disabled = state !== "running"; });
    q("[data-world-title]").textContent = state === "won" ? "You brought it to life." : state === "paused" ? "A little pause." : "Your turn.";
    q("[data-world-description]").textContent = state === "won" ? "Six cells. One little world. Built from code." : state === "paused" ? "Your progress is safe. Resume when ready." : "Catch 6 energy cells. Reach the portal.";
    start.textContent = !sprites ? "Loading scene…" : state === "won" ? "Play again" : state === "paused" ? "Resume" : "Play demo";
    if (state === "won") q("[data-world-announcement]").textContent = "You reached the portal. Demo complete. Play again or continue to services.";
  };
  const stop = () => { cancelAnimationFrame(frame); frame = 0; last = 0; held = 0; keys.clear(); };
  const pause = () => { stop(); pauseCircuit(model); sync(); draw(); };
  const tick = now => {
    frame = 0;
    if (model.state !== "running" || !enabled || !inView || document.hidden) { pause(); return; }
    const dt = last ? (now - last) / 1000 : 0;
    last = now;
    const direction = held || Number(keys.has("ArrowRight") || keys.has("d")) - Number(keys.has("ArrowLeft") || keys.has("a"));
    stepCircuit(model, dt, direction, width < 600 ? 0.09 : 0.065);
    sync(); draw();
    if (model.state === "running") frame = requestAnimationFrame(tick);
    else { stop(); start.focus({ preventScroll: true }); }
  };
  const play = () => {
    if (!sprites || !enabled || !inView || document.hidden || !ctx) return;
    stop(); startCircuit(model); sync(); draw(); canvas.focus({ preventScroll: true }); frame = requestAnimationFrame(tick);
  };
  const resize = () => {
    width = canvas.clientWidth; height = canvas.clientHeight;
    if (!width || !height || !ctx) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0); geometryChanged(); draw();
  };
  const load = () => {
    if (loading || disposed || !ctx) return;
    loading = true;
    const bg = q("[data-world-background]"); bg.src = root.dataset.background;
    const image = new Image();
    image.onload = () => {
      if (disposed) return;
      try {
        sprites = Object.fromEntries(Object.entries(atlasRegions).map(([name, rect]) => [name, extract(image, rect)]));
        start.disabled = false; lastState = ""; root.dataset.assets = "ready"; geometryChanged(); sync(); draw();
      } catch { failed(); }
    };
    const failed = () => {
      if (disposed) return;
      root.dataset.assets = "error"; start.disabled = true; start.textContent = "Scene unavailable";
      q("[data-world-description]").textContent = "Please reload to try again, or continue to services.";
    };
    image.onerror = failed; image.src = root.dataset.atlas;
  };
  on(start, "click", play);
  on(pauseButton, "click", () => model.state === "running" ? pause() : play());
  on(canvas, "keydown", event => {
    if (model.state !== "running") return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    if (["ArrowLeft", "ArrowRight", "a", "d"].includes(key)) { event.preventDefault(); keys.add(key); }
    if (key === "Escape" || key === "p") { event.preventDefault(); pause(); start.focus({ preventScroll: true }); }
  });
  on(canvas, "keyup", event => keys.delete(event.key.length === 1 ? event.key.toLowerCase() : event.key));
  on(canvas, "blur", () => keys.clear());
  const point = event => {
    if (model.state !== "running") return;
    const rect = canvas.getBoundingClientRect(); model.target = clampX((event.clientX - rect.left) / rect.width);
  };
  on(canvas, "pointerdown", event => { point(event); if (model.state === "running") canvas.setPointerCapture(event.pointerId); });
  on(canvas, "pointermove", event => { if (event.pointerType === "mouse" || event.buttons) point(event); });
  on(canvas, "pointercancel", () => { held = 0; });
  moves.forEach(button => {
    on(button, "pointerdown", event => { if (model.state === "running") { held = Number(button.dataset.worldMove); button.setPointerCapture(event.pointerId); } });
    ["pointerup", "pointercancel", "lostpointercapture"].forEach(name => on(button, name, () => { held = 0; }));
    on(button, "click", () => { if (model.state === "running") model.target = clampX(model.target + Number(button.dataset.worldMove) * 0.07); });
  });
  on(window, "blur", pause);
  on(document, "visibilitychange", () => { if (document.hidden) pause(); });
  const observer = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting && entry.intersectionRatio >= 0.25; if (!inView) pause(); }, { threshold: [0, 0.25] });
  observer.observe(canvas);
  const lazy = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { load(); lazy.disconnect(); } }, { rootMargin: "600px" });
  lazy.observe(root);
  const sizeObserver = new ResizeObserver(resize); sizeObserver.observe(canvas);
  sync(); resize();
  if (!ctx) { root.dataset.assets = "unsupported"; start.disabled = true; start.textContent = "Canvas unavailable"; q("[data-world-description]").textContent = "Please use a Canvas-enabled browser, or continue to services."; }
  return {
    pause,
    drawProgress(value) { progress = value; draw(); },
    setEnabled(value) { if (enabled === value) return; enabled = value; if (!value) pause(); sync(); },
    constructionPaths() {
      const b = boxes(), travel = height * 1.12;
      const paths = Object.fromEntries(Object.entries(b).map(([name, rect]) => [name, contourFor(name, rect).map(point => ({ x: point.x, y: point.y + travel }))]));
      paths.energyCopies = [0.36, 0.52, 0.68].map(x => contourFor("energy", { ...b.energy, x: x * width - b.energy.w / 2 }).map(point => ({ x: point.x, y: point.y + travel })));
      return paths;
    },
    destroy() { disposed = true; stop(); observer.disconnect(); lazy.disconnect(); sizeObserver.disconnect(); removers.forEach(remove => remove()); },
  };
}
